import { Info, Photo } from "../Icons";
import { useTranslations } from "@/contexts/Translation";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import CardStats from "./CardStats";
import { IconButton } from "../Button/Button";
import CopyValue from "../CopyValue/CopyValue";
import { Card } from "@/types";

import Modal from "../Modal/Modal";
import FeedbachCard from "./JourneyCard";
import ScreenshotView from "./ScreenshotView";
import CardRecommendations from "./CardRecommendations";
import CardVerification from "./CardVerification";
import { getCardUrl } from "@/lib/getCardUrl";
import { useScreenshot } from "@/lib/useScreenshot";
import { useApiData } from "@/contexts/ApiData";
import { HelpIcon, HelpText } from "../Forms/Input";
import styles from "./JourneyCardView.module.css";

const JourneyCardView = ({
  card,
  isPreview = false,
  setImage,
  isPublic,
  refreshImage,
  showVisits,
}: {
  card: Card;
  isPreview?: boolean;
  setImage?: (img: string | null) => void;
  isPublic?: boolean;
  refreshImage?: boolean;
  showVisits?: boolean;
}) => {
  const t = useTranslations("card");
  const [showInfo, setShowInfo] = useState(false);
  const [showScreenshot, setShowScreenshot] = useState(false);
  const cardUrl = getCardUrl(card.cardId);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [image, takeScreenshot] = useScreenshot();
  const { put } = useApiData();
  const refreshed = useRef(false);
  const visitsHelpId = useId();
  const [visitsHelpOpen, setVisitsHelpOpen] = useState(false);
  const closeVisitsHelp = useCallback(() => setVisitsHelpOpen(false), []);

  const toggleInfo = () => {
    setShowInfo((current) => !current);
  };

  useEffect(() => {
    if (card && isPreview && cardRef.current) {
      takeScreenshot(cardRef.current);
    }
  }, [card, isPreview, takeScreenshot]);

  // The stored image of an older card shows a design that is gone. Take a new one when the owner
  // opens the card, but only at full width (768 px): a scaled-down card would give a smaller image
  // than the one made when the card was created. If anything fails, the old image stays.
  useEffect(() => {
    const node = cardRef.current;
    if (!refreshImage || !card.imageOutdated || !node || refreshed.current) return;
    if (node.getBoundingClientRect().width < 767) return;
    refreshed.current = true;
    takeScreenshot(node)
      .then((img) => img && put(`/card/${card.cardId}/image`, { image: img }))
      .catch(() => undefined);
  }, [refreshImage, card.imageOutdated, card.cardId, put, takeScreenshot]);

  useEffect(() => {
    if (setImage) {
      setImage(image);
    }
  }, [setImage, image]);

  const openScreenshotMode = () => {
    setShowScreenshot(true);
  };

  return (
    <>
      <FeedbachCard ref={cardRef} card={card} />
      {isPublic && <CardVerification card={card} />}
      {(isPublic || isPreview) && <CardRecommendations recommendations={card.recommendations} />}
      {!isPreview && (
        <div className={styles.controls}>
          {showVisits && (
            <div className={styles.visits} data-testid="card-visits">
              <span>{t("visits", card.visits ?? 0, card.qrScans ?? 0)}</span>
              <HelpIcon text={t("visitsHelp")} isOpen={visitsHelpOpen} setIsOpen={setVisitsHelpOpen} controls={visitsHelpId} />
              <HelpText text={t("visitsHelp")} isOpen={visitsHelpOpen} id={visitsHelpId} onClose={closeVisitsHelp} />
            </div>
          )}
          {!isPublic && (
            <>
              <CopyValue variant='compact' value={cardUrl} />
              <IconButton onClick={() => openScreenshotMode()} title={t("Screenshot")}>
                <Photo height={"20px"} width={"20px"} />
              </IconButton>
            </>
          )}
          <IconButton onClick={toggleInfo} title={t("moreInfo")}>
            <Info height={"20px"} width={"20px"} />
          </IconButton>
        </div>
      )}
      {!isPublic && showScreenshot && (
        <Modal onClose={() => setShowScreenshot(false)}>
          <ScreenshotView card={card} />
        </Modal>
      )}
      {showInfo && (
        <Modal onClose={toggleInfo}>
          <div className={styles.infoModal}>
            <CardStats isOpen card={card} isPreview={isPreview} />
            {!isPublic && <CardRecommendations recommendations={card.recommendations} />}
          </div>
        </Modal>
      )}
    </>
  );
};

export default JourneyCardView;
